import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { setCurrency } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

async function syncCurrency() {
  try {
    const { data } = await axios.get(`${API}/settings/business`, { withCredentials: true });
    if (data?.currency_symbol) setCurrency(data.currency_symbol);
  } catch { /* ignore — default stays £ */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      syncCurrency();
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(data);
    syncCurrency();
    return data;
  };

  const register = async (email, password, name, businessName, referralCode, promoCode) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name, business_name: businessName || '', referral_code: referralCode || '', promo_code: promoCode || '' }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  const hasPermission = useCallback((area, key) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'superadmin') return true;
    const areaPerms = user.permissions?.[area];
    if (!areaPerms || !areaPerms.enabled) return false;
    return !!areaPerms[key];
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
